import { z } from "zod";
import { Taxonomy } from "../categories";
import { FinancialDocumentBulkActionTypes } from "../constants";
import { AccountInfoExtraction } from "../schemas/financialDocument/account-info-extraction.schema";
import { BalanceSheetExtractionRecord } from "../schemas/financialDocument/balance-sheet-extraction-record.schema";
import { CoaExtractionRecord } from "../schemas/financialDocument/coa-extraction-record.schema";
import { GlExtractionRecord } from "../schemas/financialDocument/gl-extraction-record.schema";
import { DocumentInvoicesExtractionRecord } from "../schemas/financialDocument/document-invoices-extraction-record.schema";
import { DocumentBillsExtractionRecord } from "../schemas/financialDocument/document-bills-extraction-record.schema";
import {
    AccountType,
    AccountSubtype,
} from "../constants/domains/accounting/accounts";
import { AccountingLedgerBasis } from "./domains/accounting/ledger";
import { EagerLoaded } from "./base/orm";
import {
    DocumentCategoryBase,
    DocumentCategory as DocumentCategoryEntityType,
} from "./documentCategory";
import { Entity, EntityBase, EntityWithUser } from "./entity";
import { Counterparty } from "./counterparty";
import {
    IntegrationAccount,
    IntegrationAccountBase,
} from "./integrationAccount";
import { TransactionMatch, TransactionMatchBase } from "./transactionMatch";
import {
    FinancialAccount,
    FinancialAccountBase,
    FinancialAccountType,
} from "./financialAccount";
import { User, UserBase } from "./user";

export enum ExtractionStatus {
    NOT_STARTED = "NOT_STARTED",
    IN_PROGRESS = "IN_PROGRESS",
    COMPLETED = "COMPLETED",
    FAILED = "FAILED",
    REJECTED = "REJECTED",
}

export const DocumentTaxonomyExtractionSchema = z.object({
    periodStartDate: z.string().date().nullable().default(null),
    periodEndDate: z.string().date().nullable().default(null),
    accountNumber: z.string().nullable().default(null),
    institutionName: z.string().nullable().default(null),
    otherAccountIdentifiers: z.string().nullable().default(null),
});

export const FinancialDocumentSchema = z.object({
    id: z.coerce.number(),
    fileName: z.string(),
    path: z.string(),
    createdAt: z.coerce.date(),
    source: z.string(),
});

export type FinancialDocumentFromSchema = z.infer<
    typeof FinancialDocumentSchema
>;

export type LinkedJournalEntrySource = "manual" | "opening_balance";

/**
 * A journal entry a document is attached to, slimmed down to what the
 * documents details panel needs to render a row and open the JE drawer.
 * The drawer fetches full lines itself via the journal entry endpoint.
 */
export interface LinkedJournalEntry {
    referenceNumber: string;
    entityId: number;
    date: string;
    memo: string | null;
    ledgerId: string;
    ledgerBasis: AccountingLedgerBasis;
    source: LinkedJournalEntrySource;
}

export interface LinkedAccrualDocument {
    id: string;
    entityId: number;
    invoiceNumber: string | null;
    date: string;
    counterpartyName: string | null;
    description: string | null;
}

export type LinkedInvoice = LinkedAccrualDocument;
export type LinkedBill = LinkedAccrualDocument;

export type DocumentInvoicesExtractionScalars = Pick<
    DocumentInvoicesExtractionRecord,
    "status" | "isChangedByUser" | "originalExtraction"
>;

export type DocumentBillsExtractionScalars = Pick<
    DocumentBillsExtractionRecord,
    "status" | "isChangedByUser" | "originalExtraction"
>;

export interface FinancialDocumentLinkedItems {
    linkedJournalEntries: LinkedJournalEntry[];
    linkedInvoices: LinkedInvoice[];
    linkedBills: LinkedBill[];
}

/**
 * Scalar fields for FinancialDocument (no relations).
 */
interface FinancialDocumentScalars extends FinancialDocumentFromSchema {
    date: Date | null;
    amount: number | null;
    originalFileName: string | null;
    hasAttemptedMatching: boolean;
    ocrSuccessful: boolean;

    isSpreadsheet: boolean | null; // Replace with file type: receipt, spreadsheet, etc.
    numTransactions: number | null;
    importerErrors: ImporterErrors[] | null;
    dismissedImporterErrors: ImporterErrors[] | null;
    transactionsImportStatus: TransactionsImportStatus | null;

    // Put in CSV metadata JSON
    spreadsheetImporterStatus: SpreadsheetImporterStatus; // csvImporterStatus
    csvFormatStr: string | null;
    spreadsheetParsingRuleId: number | null;
    entityId: number | null;
    financialAccountId: number | null;
    integrationAccountId: string | null;
    accountInfoExtracted: boolean;
    taxonomyExtraction: DocumentTaxonomyExtraction | null;
    taxonomyExtractionStatus: ExtractionStatus;
    loanExtraction: LoanStatementExtraction | null;
    loanExtracted: boolean;
    openingBalance: number | null;
    closingBalance: number | null;
    openingDate: string | null;
    closingDate: string | null;
    transactionsSpreadsheetId: string | null;
    externalExtendFileId: string | null;
    externalExtendLightParseFileId: string | null;
    importerQueue: string | null;
    documentCategoryId: string | null;
    // One document can be attached to at most one counterparty; a
    // counterparty can have many attached documents (reverse of this FK).
    counterpartyId: string | null;
}

/**
 * Computed fields populated by queries, not stored as DB columns.
 */
interface FinancialDocumentComputedFields {
    isReconciled?: boolean;
    cashAppExcludedLinkedAccountPaymentCount?: number | null;
    accrualReviewStatus?: AccrualReviewStatus | null;
}

/**
 * Slim counterparty projection loaded for financial-document rows.
 * Only id + name are exposed (see getFinancialDocumentListQuery).
 */
export interface FinancialDocumentCounterparty {
    id: string;
    name: string;
}

/**
 * Base type for FinancialDocument - allows undefined on relations.
 * Used by entity classes (TypeORM).
 */
export interface FinancialDocumentBase extends FinancialDocumentScalars {
    entity?: EntityBase | null;
    financialAccount?: FinancialAccountBase | null;
    transactionMatches?: TransactionMatchBase[];
    integrationAccount?: IntegrationAccountBase | null;
    uploader?: UserBase | null;
    documentCategory?: DocumentCategoryBase | null;
    counterparty?: FinancialDocumentCounterparty | null;
    bankReconciliationDocuments?: Array<{ financialDocumentId: number }>;
    accountInfoExtraction?: AccountInfoExtraction | null;
    balanceSheetExtraction?: BalanceSheetExtractionRecord | null;
    coaExtraction?: CoaExtractionRecord | null;
    glExtraction?: GlExtractionRecord | null;
    documentInvoicesExtraction?: DocumentInvoicesExtractionScalars | null;
    documentBillsExtraction?: DocumentBillsExtractionScalars | null;
    importerOutput?: object | null;
}

/**
 * Main frontend type for FinancialDocument - scalars only, no relations.
 * Used when relations are not loaded (e.g., get-documents-for-financial-account,
 * nested in TransactionMatchDto, IntegrationAccountDto).
 */
export type FinancialDocument = FinancialDocumentScalars &
    FinancialDocumentComputedFields;

/**
 * Extended frontend type for FinancialDocument - with REQUIRED relations.
 * Used by DTOs when relations are always loaded (all financial-document module endpoints).
 * Relations are required (not optional) because the backend always loads them for these endpoints.
 */
export interface FinancialDocumentExtended
    extends FinancialDocumentScalars, FinancialDocumentComputedFields {
    entity: EntityWithUser | null;
    financialAccount: FinancialAccount | null;
    transactionMatches: Array<
        EagerLoaded<TransactionMatch, "financialTransaction">
    >;
    integrationAccount: IntegrationAccount | null;
    uploader: User | null;
    documentCategory: DocumentCategoryEntityType | null;
    counterparty: FinancialDocumentCounterparty | null;
    accountInfoExtraction: AccountInfoExtraction | null;
    balanceSheetExtraction: BalanceSheetExtractionRecord | null;
    coaExtraction: CoaExtractionRecord | null;
    glExtraction: GlExtractionRecord | null;
    documentInvoicesExtraction: DocumentInvoicesExtractionRecord | null;
    documentBillsExtraction: DocumentBillsExtractionRecord | null;
    linkedJournalEntries: LinkedJournalEntry[];
    linkedInvoices: LinkedInvoice[];
    linkedBills: LinkedBill[];
}

export function isFinancialDocumentExtended(
    document:
        | FinancialDocument
        | FinancialDocumentListItem
        | FinancialDocumentExtended,
): document is FinancialDocumentExtended {
    return "linkedJournalEntries" in document;
}

export interface FinancialDocumentListItemIntegrationAccount {
    id: string;
    financialAccountId: number | null;
}

export interface FinancialDocumentListItemAccrualExtractionStatus {
    status: ExtractionStatus;
}

/**
 * Slim list-row type for GET /financial-document.
 * Omits detail-only relations (linked items, full extraction graphs).
 */
export interface FinancialDocumentListItem
    extends FinancialDocumentScalars, FinancialDocumentComputedFields {
    entity: Entity | null;
    financialAccount: FinancialAccount | null;
    transactionMatches: Array<
        EagerLoaded<TransactionMatch, "financialTransaction">
    >;
    integrationAccount: FinancialDocumentListItemIntegrationAccount | null;
    uploader: User | null;
    documentCategory: DocumentCategoryEntityType | null;
    counterparty: FinancialDocumentCounterparty | null;
    documentInvoicesExtraction: FinancialDocumentListItemAccrualExtractionStatus | null;
    documentBillsExtraction: FinancialDocumentListItemAccrualExtractionStatus | null;
}

export interface RemoveAction {
    type: FinancialDocumentBulkActionTypes.REMOVE;
    confirmTransactionsRemoval?: boolean;
    skipReconciledDocuments?: boolean;
}

export interface AssignToEntityAction {
    type: FinancialDocumentBulkActionTypes.ASSIGN_ENTITY;
    entityId: number;
}

export interface EditDocumentCategoryAction {
    type: FinancialDocumentBulkActionTypes.EDIT_DOCUMENT_CATEGORY;
    documentCategoryId: string | null;
}

export interface EditDocumentCategoryActionWithRelation {
    type: FinancialDocumentBulkActionTypes.EDIT_DOCUMENT_CATEGORY;
    documentCategory: DocumentCategoryEntityType | null;
}

export interface AddToImporterAction {
    type: FinancialDocumentBulkActionTypes.ADD_TO_IMPORTER;
    forceRerun?: boolean;
}

export interface UnmatchTransactionsAction {
    type: FinancialDocumentBulkActionTypes.UNMATCH_TRANSACTIONS;
}

export interface AssignToFinancialAccountAction {
    type: FinancialDocumentBulkActionTypes.ASSIGN_FINANCIAL_ACCOUNT;
    financialAccountId: number | null;
}

export interface AssignCounterpartyAction {
    type: FinancialDocumentBulkActionTypes.ASSIGN_COUNTERPARTY;
    counterpartyId: string | null;
}

export interface AssignCounterpartyActionWithRelation {
    type: FinancialDocumentBulkActionTypes.ASSIGN_COUNTERPARTY;
    counterparty: Counterparty | null;
}

export interface RevertRenameAction {
    type: FinancialDocumentBulkActionTypes.REVERT_RENAME;
}

export type FinancialDocumentBulkAction =
    | RemoveAction
    | AssignToEntityAction
    | EditDocumentCategoryAction
    | AddToImporterAction
    | UnmatchTransactionsAction
    | AssignToFinancialAccountAction
    | AssignCounterpartyAction
    | RevertRenameAction;

export type FinancialDocumentBulkActionWithRelation =
    | RemoveAction
    | AssignToEntityAction
    | EditDocumentCategoryActionWithRelation
    | AddToImporterAction
    | UnmatchTransactionsAction
    | AssignToFinancialAccountAction
    | AssignCounterpartyActionWithRelation
    | RevertRenameAction;

export enum SpreadsheetImporterStatus {
    NOT_IMPORTED = "not_imported",
    NEEDS_VERIFICATION = "needs_verification",
    IMPORT_RUNNING = "currently_importing",
    IMPORT_FAILED = "import_failed",
    IMPORT_SUCCESS = "import_success",
}

export enum TransactionsImportStatus {
    SCHEDULED = "SCHEDULED",
    IN_PROGRESS = "IN_PROGRESS",
    FINISHED = "FINISHED",
    FAILED = "FAILED",
}

export enum ImporterErrors {
    NOT_RECONCILED = "not_reconciled",
    RECONCILED_WITH_INDETERMINABLE_SIGNS = "reconciled_with_indeterminable_signs",
    NOT_CONFIDENT = "not_confident",
    FOREIGN_CURRENCY = "foreign_currency",
    SAME_PERIOD_ALREADY_UPLOADED = "same_period_already_uploaded",
    DUPLICATE_FILE_UPLOADED = "duplicate_file_uploaded",
    STATEMENT_PERIOD_ALREADY_RECONCILED = "statement_period_already_reconciled",
    INVALID_TRANSACTIONS = "invalid_transactions",
    ASSIGNED_FINANCIAL_ACCOUNT_MISMATCH = "assigned_financial_account_mismatch",
    BALANCES_OR_DATES_MISSING = "balances_or_dates_missing",
    OPENING_DATE_AFTER_CLOSING_DATE = "opening_date_after_closing_date",
    EXTRACTION_FAILED = "extraction_failed",
    PASSWORD_PROTECTED_PDF = "password_protected_pdf",
    PERIOD_IS_LOCKED = "period_is_locked",
    TRANSACTIONS_OUTSIDE_PERIOD = "transactions_outside_period",
    TRANSACTIONS_BEFORE_BOOKKEEPING_START = "transactions_before_bookkeeping_start",
    TRANSACTIONS_AFTER_BOOKKEEPING_END = "transactions_after_bookkeeping_end",
    TRANSACTIONS_AFTER_ACCOUNT_BOOKKEEPING_END = "transactions_after_account_bookkeeping_end",
    ACCRUAL_MISSING_ENTITY = "accrual_missing_entity",
    ACCRUAL_MISSING_COUNTERPARTY = "accrual_missing_counterparty",
    ACCRUAL_NO_LINE_ITEMS = "accrual_no_line_items",
    ACCRUAL_LINE_ITEMS_DONT_SUM_TO_TOTAL = "accrual_line_items_dont_sum_to_total",
    ACCRUAL_POSSIBLE_DUPLICATE = "accrual_possible_duplicate",
    ACCRUAL_WRONG_WORKSPACE = "accrual_wrong_workspace",
    ACCRUAL_UNREADABLE_FILE = "accrual_unreadable_file",
    ACCRUAL_NOT_ACCRUAL_DOCUMENT = "accrual_not_accrual_document",
    ACCRUAL_UNCONFIRMED_COUNTERPARTY = "accrual_unconfirmed_counterparty",
    ACCRUAL_COUNTERPARTY_DETAILS_CHANGED = "accrual_counterparty_details_changed",
    ACCRUAL_WRONG_ENTITY = "accrual_wrong_entity",
    ACCRUAL_WRONG_DOCUMENT_TYPE = "accrual_wrong_document_type",
    ACCRUAL_PARTIALLY_PAID = "accrual_partially_paid",
    ACCRUAL_MISSING_DOCUMENT_NUMBER = "accrual_missing_document_number",
    ACCRUAL_MISSING_DUE_DATE = "accrual_missing_due_date",
    ACCRUAL_DUE_DATE_BEFORE_DOCUMENT_DATE = "accrual_due_date_before_document_date",
    ACCRUAL_MISSING_ACCOUNT = "accrual_missing_account",
    ACCRUAL_UNCONFIRMED_ACCOUNT = "accrual_unconfirmed_account",
    ACCRUAL_UNCONFIRMED_ITEM = "accrual_unconfirmed_item",
    CASH_APP_EXCLUDED_LINKED_PAYMENTS = "cash_app_excluded_linked_payments",
}

export enum AccrualReviewStatus {
    WRONG_WORKSPACE = "wrong_workspace",
    WRONG_ENTITY = "wrong_entity",
    NOT_ACCRUAL_DOCUMENT = "not_accrual_document",
    EXTRACTION_FAILED = "extraction_failed",
    MISSING_FIELDS = "missing_fields",
    TOTALS_MISMATCH = "totals_mismatch",
    CONFIRM_COUNTERPARTY = "confirm_counterparty",
    COUNTERPARTY_REQUIRED = "counterparty_required",
    COUNTERPARTY_CHANGED = "counterparty_changed",
    DUPLICATE = "duplicate",
    FOREIGN_CURRENCY = "foreign_currency",
    PERIOD_LOCKED = "period_locked",
    PARTIALLY_PAID = "partially_paid",
    CONFIRM_ACCOUNTS = "confirm_accounts",
    CONFIRM_ITEMS = "confirm_items",
    MULTIPLE_ISSUES = "multiple_issues",
}

export enum AccrualDraftCounterpartyState {
    MATCHED = "matched",
    NEEDS_CONFIRMATION = "needs_confirmation",
    EMPTY = "empty",
}

export const UNRECOVERABLE_IMPORTER_ERRORS = new Set([
    ImporterErrors.PASSWORD_PROTECTED_PDF,
    ImporterErrors.PERIOD_IS_LOCKED,
]);

export const TRANSACTION_BEFORE_PERIOD_TOLERANCE_DAYS = 7;
export const TRANSACTION_AFTER_PERIOD_TOLERANCE_DAYS = 2;

export interface ReceiptOCRResponse {
    totalAmount: {
        data?: number;
    };
    date: {
        data?: string;
    };
    merchantName: {
        data?: string;
    };
}

export interface ReceiptData {
    amount: number; // in dollars
    date: Date;
    merchant?: string;
    fullResponse?: any;
}

export interface LoanPaymentTransaction {
    date: Date;
    isScheduled: boolean;
    net: number;
    principal: number;
    interest: number;
    escrow: number;
    fees: number;
    other: number;
    outstandingBalance: number | null;
}

export interface LoanOrigination {
    openingBalance: number;
    openingBalanceDate: Date;
}

export interface LoanStatementExtraction {
    transactions: LoanPaymentTransaction[];
    loanOrigination: LoanOrigination | null;
}

export enum ExtractionModifiedBy {
    AI = "ai",
    USER = "user",
}

/**
 * Provenance of a CoA/GL extraction. The dedicated extraction tables are
 * generic and will eventually hold both deterministic parser output (Xero)
 * and AI-based extraction, which have very different trust characteristics.
 */
export enum ExtractionMethod {
    AI = "AI",
    XERO = "XERO",
    QBDT = "QBDT",
    QBO = "QBO",
}

/**
 * Accounting basis of a persisted GL extraction. The cash/accrual values match
 * the GL slot keys so the frontend can restore a GL document into the correct
 * upload slot. `UNKNOWN` is parsed for label-less layouts that state no basis;
 * such a GL is not migratable until the user supplies a basis at finalize.
 */
export enum GlExtractionBasis {
    CASH = "cashGl",
    ACCRUAL = "accrualGl",
    UNKNOWN = "unknown",
}

export type DocumentTaxonomyExtraction = z.infer<
    typeof DocumentTaxonomyExtractionSchema
>;

// TODO: This should be stored as a DraftEntity according to Doc Extraction Tech Spec.
export interface AccountMapping {
    originalAccountName: string;
    mappedAccountType?: AccountType;
    mappedAccountSubtype?: AccountSubtype;
    mappedAccountName?: string;
}

export interface CoAMappingExtraction {
    accountMappings: AccountMapping[];
}

export interface Split {
    amount: number;
    splitName: string;
    category?: Taxonomy;
}

export interface BankStatementTransaction {
    date: string | null;
    description: string;
    amount: number;
    sourceData?: string;
    splits?: Split[];
}

// TODO: This should be stored as a DraftEntity according to Doc Extraction Tech Spec.
// For now, only saved in Temporal flow that updates Spreadsheet - until we refactor importer.
export interface BankStatementExtraction {
    transactions: BankStatementTransaction[];
    openingBalance?: number;
    closingBalance?: number;
    openingDate?: string;
    closingDate?: string;
    accountIdentifier?: string;
    accountType?: FinancialAccountType;
    cashAppExcludedLinkedAccountPaymentCount?: number;
}
